import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useAuth } from "../../store/hooks/useAuth";
import { Button } from "../common";
import { getErrorMessage } from "../../store/utils/apiError";
import AuthLayout, { DEFAULT_ORG, getOrgCodeFromSubdomain } from "./AuthLayout";

const FIELD_WRAPPER =
  "flex h-12 items-center gap-2.5 rounded-md border border-border-primary bg-surface-muted px-3.5 transition-all duration-150 focus-within:border-border-focus focus-within:bg-surface focus-within:ring-[3px] focus-within:ring-[color:var(--color-focus-ring)]";

const FIELD_INPUT =
  "h-full w-full border-0 bg-transparent p-0 text-body-md text-text-primary outline-none placeholder:text-text-placeholder focus:outline-none focus:ring-0";

const FIELD_ICON = "h-[18px] w-[18px] shrink-0 text-text-tertiary";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const queryCode = useMemo(
    () => getOrgCodeFromSubdomain() ?? DEFAULT_ORG.queryCode,
    [],
  );
  const orgCodeToSend = queryCode;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await login.mutate({
      workEmail: username,
      password,
      orgCode: orgCodeToSend,
    });
  };

  return (
    <AuthLayout>
      <div className="mb-8">
        <h1 className="text-heading-xl text-text-primary">Welcome back</h1>
        <p className="text-body-md mt-2 text-text-secondary">
          Sign in to your workspace
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="username"
            className="text-label mb-2 block text-[var(--color-text-label)]"
          >
            Work email <span className="text-error">*</span>
          </label>
          <div className={FIELD_WRAPPER}>
            <Mail aria-hidden="true" className={FIELD_ICON} />
            <input
              id="username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={FIELD_INPUT}
              placeholder="Enter your work email"
              autoComplete="username"
              aria-invalid={login.isError || undefined}
              aria-describedby={login.isError ? "login-error" : undefined}
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="password"
            className="text-label mb-2 block text-[var(--color-text-label)]"
          >
            Password <span className="text-error">*</span>
          </label>
          <div className={FIELD_WRAPPER}>
            <Lock aria-hidden="true" className={FIELD_ICON} />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD_INPUT}
              placeholder="Enter your password"
              autoComplete="current-password"
              aria-invalid={login.isError || undefined}
              aria-describedby={login.isError ? "login-error" : undefined}
              required
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((v) => !v)}
              className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)]"
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" className="h-[17px] w-[17px]" />
              ) : (
                <Eye aria-hidden="true" className="h-[17px] w-[17px]" />
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-label rounded-sm font-bold text-primary-700 transition-colors hover:text-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)]"
          >
            Forgot password?
          </button>
        </div>

        {login.isError && (
          <div
            id="login-error"
            role="alert"
            className="text-body-base animate-fade-in rounded-md border border-[var(--color-error-border)] bg-error-light px-3.5 py-2.5 font-medium text-error-dark"
          >
            {getErrorMessage(login.error)}
          </div>
        )}

        <Button
          htmlType="submit"
          appearance="primary"
          size="large"
          disabled={login.isLoading || !username.trim() || !password.trim()}
          className="!h-[50px] w-full !rounded-md"
        >
          {/* Single child: antd wraps fragments in its own <span>, which turns
              the icon into an inline element and breaks flex alignment. */}
          <span className="inline-flex items-center gap-2 leading-none">
            {login.isLoading ? (
              <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <span>Sign in</span>
                <ArrowRight aria-hidden="true" className="h-[18px] w-[18px]" />
              </>
            )}
          </span>
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
